import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, Link2, MoreHorizontal, Tags, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import type { PhotosFilters } from '../lib/filters'
import type { PhotoUi } from '../lib/permissions'
import PhotosFilterBar from './PhotosFilterBar'
import TagManagerDialog from './TagManagerDialog'

export type PhotosView = 'timeline' | 'albums'
export type ThumbSize = 'S' | 'M' | 'L'
const SIZES: ThumbSize[] = ['S', 'M', 'L']

interface Props {
  projectId: string
  view: PhotosView
  onViewChange: (view: PhotosView) => void
  filters: PhotosFilters
  onFiltersChange: (filters: PhotosFilters) => void
  size: ThumbSize
  onSizeChange: (size: ThumbSize) => void
  ui: PhotoUi
  onUpload?: () => void
  onReport?: () => void
  onShowShares?: () => void
}

const PhotosToolbar: React.FC<Props> = ({ projectId, view, onViewChange, filters, onFiltersChange, size, onSizeChange, ui, onUpload, onReport, onShowShares }) => {
  const { t } = useTranslation()
  const [tagsOpen, setTagsOpen] = useState(false)
  return (
    <div className="flex flex-wrap items-center gap-3 py-2">
      <Tabs value={view} onValueChange={(v) => onViewChange(v as PhotosView)}>
        <TabsList>
          <TabsTrigger value="timeline">{t('Photos_tab_timeline')}</TabsTrigger>
          <TabsTrigger value="albums">{t('Photos_tab_albums')}</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="min-w-0 flex-1">
        <PhotosFilterBar projectId={projectId} filters={filters} onFiltersChange={onFiltersChange} />
      </div>

      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1" role="group" aria-label={t('Photos_thumb_size')}>
          {SIZES.map((s) => (
            <Button
              key={s}
              variant="outline"
              size="sm"
              aria-pressed={size === s}
              className={cn('h-8 w-8 p-0', size === s && 'bg-accent text-accent-foreground')}
              onClick={() => onSizeChange(s)}
            >
              {s}
            </Button>
          ))}
        </div>
        {onShowShares && (
          <Button variant="outline" size="sm" className="h-8" onClick={onShowShares}>
            <Link2 className="h-4 w-4" />
            {t('Photos_shares')}
          </Button>
        )}
        {ui.canExport && onReport && (
          <Button variant="outline" size="sm" className="h-8" onClick={onReport}>
            <FileText className="h-4 w-4" />
            {t('Photos_report_button')}
          </Button>
        )}
        {onUpload && (
          <Button size="sm" className="h-8" onClick={onUpload}>
            <Upload className="h-4 w-4" />
            {t('Photos_upload')}
          </Button>
        )}
        {ui.isAdmin && (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" aria-label={t('Photos_more')}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setTagsOpen(true)}>
                  <Tags className="mr-2 h-4 w-4" />
                  {t('Photos_tags_manage')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <TagManagerDialog projectId={projectId} open={tagsOpen} onOpenChange={setTagsOpen} />
          </>
        )}
      </div>
    </div>
  )
}

export default PhotosToolbar
